import { useEffect, useRef } from 'react';
import { useGameStore } from '../store/gameStore';
import type { ClientMsg, ServerMsg, ClientRole, TeamId } from '../types/game';

const API = import.meta.env.VITE_API_URL || '/api';

export function useGameSocket(gameId: string, team: TeamId | null, role: ClientRole) {
  const wsRef = useRef<WebSocket | null>(null);
  const attemptsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const unmountedRef = useRef(false);

  useEffect(() => {
    if (!gameId || gameId === 'undefined') return;
    unmountedRef.current = false;

    const WS_BASE =
      import.meta.env.VITE_WS_URL ||
      (window.location.protocol === 'https:' ? 'wss://' : 'ws://') +
        window.location.host;

    const applyState = (state: unknown) => {
      const store = useGameStore.getState();
      store.setGame(state as Parameters<typeof store.setGame>[0]);
    };

    // Polling REST como respaldo (cada 2s)
    const startPolling = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(async () => {
        try {
          const res = await fetch(`${API}/game/${gameId}/state`);
          if (res.ok) {
            const state = await res.json();
            applyState(state);
          }
        } catch {}
      }, 2000);
    };

    const stopPolling = () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    const connect = () => {
      if (unmountedRef.current) return;

      const params = new URLSearchParams({ role, ...(team ? { team } : {}) });
      const url = `${WS_BASE}/api/game/${gameId}/ws?${params}`;

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptsRef.current = 0;
        useGameStore.getState().setConnected(true);
        useGameStore.getState().setConnectionStatus('connected');
        stopPolling(); // WS conectado, no necesitamos polling
      };

      ws.onmessage = (e) => {
        try {
          const msg: ServerMsg = JSON.parse(e.data as string);
          const store = useGameStore.getState();

          if (msg.type === 'STATE_SYNC') {
            store.setGame(msg.state);
            return;
          }

          // Parche local para mensajes individuales (opcional, STATE_SYNC ya cubre todo)
          const g = { ...store.game };
          switch (msg.type) {
            case 'BUZZER_WON':
              g.buzzer = 'locked';
              g.winner = { team: msg.winner, serverTs: msg.serverTs };
              g.blockedTeams = (['A','B','C'] as TeamId[]).filter(t => t !== msg.winner);
              g.status = 'team_answering';
              store.setGame(g);
              store.setLastEvent('BUZZER_WON', `¡Equipo ${msg.winner} ganó!`);
              break;
            case 'STEAL_ACTIVATED':
              store.setLastEvent('STEAL', '¡Modo robo!');
              break;
            case 'ROUND_END':
              store.setLastEvent('ROUND_END', msg.winner ? `Equipo ${msg.winner}` : 'Sin ganador');
              break;
            case 'RESET':
              store.setLastEvent('RESET', 'Reiniciado');
              break;
          }
        } catch {}
      };

      ws.onclose = () => {
        useGameStore.getState().setConnected(false);
        useGameStore.getState().setConnectionStatus('disconnected');
        if (unmountedRef.current) return;
        startPolling(); // WS caído, activar polling
        const delay = Math.min(500 * Math.pow(2, attemptsRef.current), 10_000);
        attemptsRef.current += 1;
        timerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => ws.close();
    };

    // Fetch inicial del estado vía REST (inmediato, no esperar WS)
    fetch(`${API}/game/${gameId}/state`)
      .then(r => r.json())
      .then(applyState)
      .catch(() => {});

    connect();

    return () => {
      unmountedRef.current = true;
      stopPolling();
      if (timerRef.current) clearTimeout(timerRef.current);
      wsRef.current?.close();
    };
  }, [gameId, role, team]);

  const send = (msg: ClientMsg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    } else {
      // WS no disponible — intentar vía REST como fallback
      console.warn('WS no conectado, mensaje descartado:', msg.type);
    }
  };

  return { send };
}